import React, { useCallback, useEffect } from 'react';
import { Check, Info, Loader2, Minus, RefreshCw, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@roger/ui-components';
import { useShallow } from 'zustand/react/shallow';

import { PermissionSlug } from '../../constants/featureFlags';
import { PAYMENT_OASIS_EXTERNAL_MESSAGE } from '../../constants/billing';
import { useStore } from '../../store';
import {
  billingOrderPath,
  patientAuthorizationsPath,
  patientEpisodePath,
  patientInfoPath,
  patientVisitPath,
} from '../../utils/navigation';
import { getStatusLabel } from '../task/taskStatus';
import type {
  BillingReadinessCategory,
  BillingReadinessReason,
  BillingReadinessTask,
  GetBillingCyclesResponse,
} from '../../services/api/types.gen';
import {
  useClaimReadiness,
  type DraftReadinessScope,
} from './ClaimReadinessProvider';
import { getClaimReadinessCategories } from './ClaimReadinessCell';
import { NOT_REQUIRED_MESSAGE, readinessSkipsOf } from './readinessSkips';
import { PaymentOasisHoldOverride } from './PaymentOasisHoldOverride';

const CorrectionLink: React.FC<{ to: string; children: React.ReactNode }> = ({
  to,
  children,
}) => (
  <Link
    to={to}
    className='font-medium text-primary underline-offset-2 hover:underline'
  >
    {children}
  </Link>
);

// A payment OASIS the previous EMR performed passes on status alone: that EMR
// holds its signature and export, so the local pass copy would be untrue.
const IMPORTED_OASIS_PASS_CONFIRMATION =
  'The payment OASIS is available and completed.';

const SKIPPED_EXPORT_OASIS_PASS_CONFIRMATION = `The payment OASIS is available. Export ${NOT_REQUIRED_MESSAGE.toLowerCase()}.`;

const PASS_CONFIRMATION: Partial<Record<BillingReadinessCategory, string>> = {
  oasis: 'The payment OASIS is available, signed and exported.',
  hipps: 'Payment OASIS and valid diagnoses are available for HIPPS.',
  visits: 'All billable visits are in a billing-ready status.',
  orders: 'All billing orders are satisfied.',
};

export const EMPTY_EVIDENCE_TEXT =
  'Details unavailable. Refresh claims to check the latest state.';
export const LOADING_TEXT = 'Loading readiness details…';
export const ERROR_TEXT = 'Unable to load readiness details. Try again.';
export const UNAVAILABLE_SCOPE_TEXT =
  'Readiness details are not available for the current list.';

/**
 * The provider-evidence side of a readiness category.
 *
 * A failing category reads its reasons from the parent-owned readiness-details
 * read; a passing category uses the row's own flags and makes no request. The
 * correction links reuse the same destinations the full preview uses, subject
 * to the same permissions, and are plain router links, so ordinary,
 * Cmd-click and Ctrl-click navigation behave as everywhere else.
 */
export const ClaimReadinessDetails: React.FC<{
  entry: GetBillingCyclesResponse;
  scope: DraftReadinessScope;
  category?: BillingReadinessCategory;
  fieldOnly?: boolean;
  /** Called after a payment-OASIS export hold is overridden here, so a caller
   * holding its own copy of this row (the draft preview) can refetch it. */
  onHoldOverridden?: () => void | Promise<void>;
}> = ({ entry, scope, category, fieldOnly = false, onHoldOverridden }) => {
  const { revision, getEntry, isScopeAdmitted, loadDetails } =
    useClaimReadiness();
  const { canViewOrders, canEditPatientInfo, canOverrideOasisHold } = useStore(
    useShallow(state => ({
      canViewOrders: state.hasPermission(PermissionSlug.ORDER_MANAGER),
      canEditPatientInfo: state.hasPermission(PermissionSlug.PATIENT_INFO_EDIT),
      canOverrideOasisHold: state.hasPermission(
        PermissionSlug.CLAIMS_OVERRIDE_OASIS_HOLD
      ),
    }))
  );

  const cycleId = entry.billing_cycle_id;
  const states = getClaimReadinessCategories(entry);
  const detail = getEntry(scope, cycleId);
  const scopeAdmitted = isScopeAdmitted(scope, cycleId);
  const fieldCodes = fieldFailureCodes(entry);
  const needsEvidence = fieldOnly
    ? fieldCodes.size > 0
    : category != null &&
      states.find(item => item.category === category)?.state === 'fail';

  useEffect(() => {
    if (!needsEvidence) return;
    // Nothing to request until the caller's current scope admits this cycle; a
    // later provider notification (registration) re-runs this effect.
    if (!isScopeAdmitted(scope, cycleId)) return;
    if (getEntry(scope, cycleId)) return;
    loadDetails(scope, cycleId);
  }, [
    needsEvidence,
    scope,
    cycleId,
    revision,
    getEntry,
    isScopeAdmitted,
    loadDetails,
  ]);

  const retry = useCallback(() => {
    void loadDetails(scope, cycleId, true);
  }, [loadDetails, scope, cycleId]);

  // The override clears the hold, so this cycle's evidence is stale. The list
  // reload itself happens in PaymentOasisHoldOverride.
  const handleHoldOverridden = useCallback(async () => {
    await Promise.all([
      loadDetails(scope, cycleId, true),
      onHoldOverridden?.(),
    ]);
  }, [loadDetails, scope, cycleId, onHoldOverridden]);

  // Informational reasons (e.g. diagnosis codes that could not be checked, or
  // an overlap with a cycle on another episode) are context, not failures:
  // they never count toward a category's verdict and are listed apart from
  // the blocking reasons, with a link where one is mapped for the code.
  const successReasons = (opts?: {
    category?: BillingReadinessCategory;
    codes?: Set<BillingReadinessReason['code']>;
    informational?: boolean;
  }): BillingReadinessReason[] => {
    if (!detail || detail.status !== 'success' || !detail.data) return [];
    const informational = opts?.informational ?? false;
    return detail.data.reasons.filter(reason => {
      if ((reason.informational ?? false) !== informational) return false;
      if (opts?.category && reason.category !== opts.category) return false;
      if (opts?.codes && !opts.codes.has(reason.code)) return false;
      return true;
    });
  };

  const renderInformational = (category: BillingReadinessCategory) => {
    const notes = successReasons({ category, informational: true });
    if (notes.length === 0) return null;
    return (
      <ul className='space-y-1'>
        {notes.map((note, index) => (
          <li
            key={`${note.code}-${index}`}
            className='flex items-start gap-1.5 text-muted-foreground'
          >
            <Info aria-hidden='true' className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            <span className='space-y-0.5'>
              <span className='block'>{note.message}</span>
              {correctionLink(note)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const renderOverrideNote = () => {
    const note = entry.payment_oasis_hold_override_note;
    if (!note) return null;
    return (
      <p
        data-testid='payment-oasis-hold-override-note'
        className='flex items-start gap-1.5 text-muted-foreground'
      >
        <Info aria-hidden='true' className='mt-0.5 h-3.5 w-3.5 shrink-0' />
        <span>{note}</span>
      </p>
    );
  };

  const renderTaskStatus = (task: BillingReadinessTask) => (
    <span className='ml-1 text-muted-foreground'>
      {getStatusLabel(task.status)}
    </span>
  );

  const renderTask = (task: BillingReadinessTask) => {
    const label =
      task.kind === 'order' && !canViewOrders ? (
        <span className='font-medium text-foreground'>{task.name}</span>
      ) : (
        <CorrectionLink
          to={
            task.kind === 'order'
              ? billingOrderPath({
                  orderId: task.task_id,
                  status: task.status,
                  patientId: entry.patient_id,
                  episodeId: entry.episode_id,
                })
              : patientVisitPath(
                  entry.patient_id,
                  task.task_id,
                  task.episode_id ?? entry.episode_id
                )
          }
        >
          {task.name}
        </CorrectionLink>
      );
    return (
      <span>
        {label}
        {renderTaskStatus(task)}
      </span>
    );
  };

  const renderTasks = (tasks: BillingReadinessTask[]) =>
    tasks.length > 0 ? (
      <ul className='mt-1 space-y-0.5'>
        {tasks.map(task => (
          <li key={task.task_id}>{renderTask(task)}</li>
        ))}
      </ul>
    ) : null;

  const correctionLink = (reason: BillingReadinessReason) => {
    switch (reason.code) {
      case 'payment_oasis':
      case 'hipps_payment_oasis':
        return (
          <CorrectionLink
            to={patientEpisodePath(entry.patient_id, entry.episode_id)}
          >
            Open episode visits
          </CorrectionLink>
        );
      // overlaps_other_episode: an informational note that the
      // cycle shares days with a cycle on another episode of the patient; the
      // episode's visits page shows both periods.
      case 'no_billable_visits':
      case 'no_visits':
      case 'overlaps_other_episode':
        return (
          <CorrectionLink
            to={patientEpisodePath(entry.patient_id, entry.episode_id)}
          >
            Review billing period visits
          </CorrectionLink>
        );
      case 'mbi':
        return canEditPatientInfo ? (
          <CorrectionLink
            to={patientInfoPath(entry.patient_id, entry.episode_id)}
          >
            Patient Info → MBI
          </CorrectionLink>
        ) : null;
      case 'pcr': {
        const field =
          detail?.status === 'success' && detail.data
            ? detail.data.pcr_field === 'pcr_number_1'
              ? 'PCR 1'
              : 'PCR 2'
            : null;
        return (
          <CorrectionLink
            to={patientEpisodePath(entry.patient_id, entry.episode_id)}
          >
            Edit episode → Pre-Claim Review{field ? ` (${field})` : ''}
          </CorrectionLink>
        );
      }
      case 'authorization':
        return (
          <CorrectionLink
            to={patientAuthorizationsPath(entry.patient_id, entry.episode_id)}
          >
            Open authorizations
          </CorrectionLink>
        );
      default:
        return null;
    }
  };

  const renderReason = (reason: BillingReadinessReason, index: number) => (
    <li key={`${reason.code}-${index}`} className='space-y-0.5'>
      <p className='text-foreground'>{reason.message}</p>
      {renderTasks(reason.tasks ?? [])}
      {correctionLink(reason)}
      {reason.code === 'payment_oasis_export_hold' && canOverrideOasisHold ? (
        <div className='pt-1'>
          <PaymentOasisHoldOverride
            billingCycleId={cycleId}
            onOverridden={handleHoldOverridden}
          />
        </div>
      ) : null}
    </li>
  );

  const renderLoading = () => (
    <div className='flex items-center gap-2 text-muted-foreground'>
      <Loader2 aria-hidden='true' className='h-3.5 w-3.5 animate-spin' />
      {LOADING_TEXT}
    </div>
  );

  const renderError = () => (
    <div className='flex flex-col gap-2'>
      <p className='text-red-800' role='alert'>
        {ERROR_TEXT}
      </p>
      <Button
        type='button'
        variant='link'
        size='sm'
        className='h-auto justify-start p-0 text-xs'
        onClick={retry}
      >
        <RefreshCw aria-hidden='true' className='mr-1 h-3 w-3' />
        Retry
      </Button>
    </div>
  );

  const renderUnavailableScope = () => (
    <p className='text-muted-foreground'>{UNAVAILABLE_SCOPE_TEXT}</p>
  );

  const renderEvidence = (opts?: {
    category?: BillingReadinessCategory;
    codes?: Set<BillingReadinessReason['code']>;
  }): React.ReactNode => {
    if (!detail) {
      // An absent entry is only a pending request when the caller's scope
      // admits one; an unregistered scope, a placeholder list or a cycle that
      // left membership can never load, so it shows unavailable evidence.
      return scopeAdmitted ? renderLoading() : renderUnavailableScope();
    }
    if (detail.status === 'loading') return renderLoading();
    if (detail.status === 'error') return renderError();
    const reasons = successReasons(opts);
    const notes = opts?.category ? renderInformational(opts.category) : null;
    return (
      <div className='space-y-2'>
        {reasons.length === 0 ? (
          <p className='text-muted-foreground'>{EMPTY_EVIDENCE_TEXT}</p>
        ) : (
          <ul className='space-y-2'>{reasons.map(renderReason)}</ul>
        )}
        {notes}
      </div>
    );
  };

  const childRow = ({
    label,
    required,
    valid,
    passText,
    content,
  }: {
    label: string;
    required: boolean;
    valid: boolean;
    passText: string;
    content?: React.ReactNode;
  }) => {
    const status = !required ? 'not_required' : valid ? 'pass' : 'fail';
    const statusPill = {
      pass: {
        text: passText,
        // Tailwind's dark: follows the OS here, but the theme tokens follow the
        // .dark class, so the dark overrides key on that class too.
        className:
          'bg-green-50 text-green-700 [.dark_&]:bg-green-950 [.dark_&]:text-green-300',
      },
      fail: {
        text: 'Not satisfied',
        className: 'bg-destructive/10 text-red-700 [.dark_&]:text-red-300',
      },
      not_required: {
        text: 'Not required',
        className: 'bg-muted text-muted-foreground',
      },
    }[status];
    const StatusIcon = { pass: Check, fail: X, not_required: Minus }[status];
    return (
      <li className='space-y-0.5'>
        <div className='flex items-center justify-between gap-2'>
          <span className='font-medium text-foreground'>{label}</span>
          <span
            data-status={status}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${statusPill.className}`}
          >
            <StatusIcon aria-hidden='true' className='h-3 w-3' />
            {statusPill.text}
          </span>
        </div>
        {required && !valid ? <ul className='space-y-0.5'>{content}</ul> : null}
      </li>
    );
  };

  const renderInsurance = (failed: boolean) => {
    let reasons: BillingReadinessReason[] = [];
    if (failed) {
      if (!detail) {
        return scopeAdmitted ? renderLoading() : renderUnavailableScope();
      }
      if (detail.status === 'loading') return renderLoading();
      if (detail.status === 'error') return renderError();
      reasons = successReasons({
        category: 'insurance',
        codes: new Set<BillingReadinessReason['code']>([
          'mbi',
          'pcr',
          'authorization',
          'missing_invoice_rate',
        ]),
      });
      if (reasons.length === 0) {
        return <p className='text-muted-foreground'>{EMPTY_EVIDENCE_TEXT}</p>;
      }
    }
    return (
      <ul className='space-y-2'>
        {childRow({
          label: 'MBI',
          required: entry.is_mbi_required,
          valid: entry.is_mbi_valid,
          passText: 'On file',
          content: reasons
            .filter(reason => reason.code === 'mbi')
            .map(renderReason),
        })}
        {childRow({
          label: 'PCR',
          required: entry.is_pcr_tracking_number_required,
          valid: entry.is_pcr_tracking_number_valid,
          passText: 'On file',
          content: reasons
            .filter(reason => reason.code === 'pcr')
            .map(renderReason),
        })}
        {childRow({
          label: 'Authorization',
          required:
            entry.is_authorization_required &&
            !readinessSkipsOf(entry).authorization,
          valid: entry.is_authorization_valid,
          passText: 'On file',
          content: reasons
            .filter(reason => reason.code === 'authorization')
            .map(renderReason),
        })}
        {entry.has_missing_invoice_rate === true
          ? childRow({
              label: 'Invoice rates',
              required: true,
              valid: false,
              passText: 'On file',
              content: reasons
                .filter(reason => reason.code === 'missing_invoice_rate')
                .map(renderReason),
            })
          : null}
      </ul>
    );
  };

  if (category === 'insurance') {
    return renderInsurance(
      states.find(item => item.category === category)?.state === 'fail'
    );
  }

  if (category) {
    const state = states.find(item => item.category === category)?.state;
    if (state === 'not_required') {
      return <p className='text-foreground'>{NOT_REQUIRED_MESSAGE}.</p>;
    }
    if (state === 'pass') {
      // An external payment OASIS passes the gate but was neither signed nor
      // exported in Roger, so it must not use the local pass confirmation.
      let text = PASS_CONFIRMATION[category];
      if (category === 'oasis' && entry.payment_oasis_status === 'external') {
        text = `${PAYMENT_OASIS_EXTERNAL_MESSAGE}.`;
      } else if (
        category === 'oasis' &&
        readinessSkipsOf(entry).payment_oasis &&
        !entry.all_oasis_correct_state
      ) {
        // Passed on the skip alone: it was not necessarily signed or exported.
        text = SKIPPED_EXPORT_OASIS_PASS_CONFIRMATION;
      } else if (category === 'oasis' && entry.is_payment_oasis_imported) {
        text = IMPORTED_OASIS_PASS_CONFIRMATION;
      }
      // A passing category makes no request, but details another category
      // already loaded may carry context for it. The export-hold override
      // rides on the row itself, so the biller sees who billed past the
      // hold, when and why, here and on every later period it covers.
      const notes = renderInformational(category);
      const overrideNote = category === 'oasis' ? renderOverrideNote() : null;
      return notes || overrideNote ? (
        <div className='space-y-2'>
          <p className='text-foreground'>{text}</p>
          {overrideNote}
          {notes}
        </div>
      ) : (
        <p className='text-foreground'>{text}</p>
      );
    }
    return renderEvidence({ category });
  }

  // Field-only mode: the draft full preview's small correction area, which
  // shows the field and missing-record links the preview sections lack.
  if (!needsEvidence) return null;
  return (
    <div className='space-y-2'>
      <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        Readiness corrections
      </p>
      {renderEvidence({ codes: fieldCodes })}
    </div>
  );
};

/**
 * The field / missing-record failures this cycle's own flags report, as reason
 * codes. They map 1:1 onto the readiness-details read's codes.
 */
// eslint-disable-next-line react-refresh/only-export-components -- exported for unit testing
export function fieldFailureCodes(
  entry: GetBillingCyclesResponse
): Set<BillingReadinessReason['code']> {
  const codes = new Set<BillingReadinessReason['code']>();
  // A skipped check fails nothing, so it asks for no evidence.
  const skips = readinessSkipsOf(entry);
  // 'external' is a selection the claim rule accepted (history the
  // previous EMR billed); it is not a failure and must not request readiness
  // details that cannot exist for it.
  if (entry.payment_oasis_status === 'external_hold') {
    // The assessment is selected and its HIPPS inputs are fine; what blocks
    // the claim is the External EMR export it is waiting on.
    if (!skips.payment_oasis) codes.add('payment_oasis_export_hold');
  } else if (
    entry.payment_oasis_status !== 'eligible' &&
    entry.payment_oasis_status !== 'external'
  ) {
    codes.add('payment_oasis');
    codes.add('hipps_payment_oasis');
  }
  if (!entry.has_computable_hipps_inputs && !skips.hipps) {
    codes.add('hipps_inputs');
  }
  if (!entry.has_billable_visits) {
    codes.add('no_billable_visits');
    codes.add('no_visits');
  }
  if (entry.is_mbi_required && !entry.is_mbi_valid) codes.add('mbi');
  if (
    entry.is_pcr_tracking_number_required &&
    !entry.is_pcr_tracking_number_valid
  ) {
    codes.add('pcr');
  }
  if (
    entry.is_authorization_required === true &&
    !entry.is_authorization_valid &&
    !skips.authorization
  ) {
    codes.add('authorization');
  }
  if (entry.has_missing_invoice_rate === true)
    codes.add('missing_invoice_rate');
  return codes;
}
